import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { PresenceProvider } from './context/PresenceContext';
import { ThemeProvider } from './context/ThemeContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { CompanySettingsProvider } from './context/CompanySettingsContext';
import { ProtectedRoute, AuthCallback } from './components/auth/ProtectedRoute';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import { MainLayout } from './components/layout/MainLayout';
import { DashboardWrapper } from './pages/dashboard/DashboardWrapper';
import { isMovingTenantUser, MOVING_BACK_OFFICE_ROLES } from './utils/movingTenant';
import { Messaging } from './pages/messaging/Messaging';
import { CrewTabs } from './pages/moving/components/CrewTabs';
import { DriverView } from './pages/driver/DriverView';
import PendingApprovalPage from './pages/auth/PendingApprovalPage';
import UnauthorizedPage from './pages/auth/UnauthorizedPage';
import LandingPage from './pages/landing/LandingPage';

// Lazy-loaded pages (not needed on initial load)
const MasterTables = lazy(() => import('./pages/master/MasterTables').then(m => ({ default: m.MasterTables })));
const EquipmentIssuesPage = lazy(() => import('./pages/equipment/EquipmentIssuesPage').then(m => ({ default: m.EquipmentIssuesPage })));
const AITriageDashboard = lazy(() => import('./pages/equipment/AITriageDashboard'));
const MechanicDashboard = lazy(() => import('./pages/mechanic/MechanicDashboard'));
const TrackDriver = lazy(() => import('./pages/tracking/TrackDriver').then(m => ({ default: m.TrackDriver })));
const DriverGPSHistory = lazy(() => import('./pages/tracking/DriverGPSHistory'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement').then(m => ({ default: m.UserManagement })));
const SuperAdminDashboard = lazy(() => import('./pages/super-admin/SuperAdminDashboard'));
const AccountingPage = lazy(() => import('./pages/accounting/AccountingPage'));
const PricingPage = lazy(() => import('./pages/pricing/PricingPage'));
const ModularPricingPage = lazy(() => import('./pages/pricing/ModularPricingPage'));
const StandalonePricingPage = lazy(() => import('./pages/pricing/StandalonePricingPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/pricing/SubscriptionSuccessPage'));
const DownloadPage = lazy(() => import('./pages/download/DownloadPage'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const AboutPage = lazy(() => import('./pages/legal/AboutPage'));
const ContactPage = lazy(() => import('./pages/legal/ContactPage'));
const TimeClockPage = lazy(() => import('./pages/standalone/TimeClockPage'));
const PayTrackPage = lazy(() => import('./pages/standalone/PayTrackPage'));
const AdminRequestsPage = lazy(() => import('./pages/settings/AdminRequestsPage'));
const SystemSettingsPage = lazy(() => import('./pages/settings/SystemSettingsPage'));
const TerminalIntegration = lazy(() => import('./pages/settings/TerminalIntegration'));
const DataMigration = lazy(() => import('./pages/settings/DataMigration'));
const EquipmentCompliance = lazy(() => import('./pages/compliance/EquipmentCompliance'));
const MarketplacePage = lazy(() => import('./pages/marketplace/MarketplacePage'));
const MarketplaceAuthPage = lazy(() => import('./pages/marketplace/MarketplaceAuthPage'));
const BookingsHistoryPage = lazy(() => import('./pages/marketplace/BookingsHistoryPage'));
const PublicMarketplacePage = lazy(() => import('./pages/marketplace/PublicMarketplacePage'));
const VendorSignupPage = lazy(() => import('./pages/marketplace/VendorSignupPage'));
const VendorAnalyticsDashboard = lazy(() => import('./pages/marketplace/VendorAnalyticsDashboard'));
const CommunityPage = lazy(() => import('./pages/marketplace/CommunityPage'));
const OwnerOperatorDashboard = lazy(() => import('./pages/owner-operator/OwnerOperatorDashboard'));
const CompanyOnboarding = lazy(() => import('./pages/onboarding/CompanyOnboarding'));
const DebugDashboard = lazy(() => import('./pages/debug/DebugDashboard'));
const CheckoutPage = lazy(() => import('./pages/marketplace/CheckoutPage').then(m => ({ default: m.CheckoutPage })));
const CheckoutSuccessPage = lazy(() => import('./pages/marketplace/CheckoutPage').then(m => ({ default: m.CheckoutSuccessPage })));
const OwnerOpSignupPage = lazy(() => import('./pages/owner-operator/OwnerOpSignupPage'));
const LoadBoardDashboard = lazy(() => import('./pages/otr').then(m => ({ default: m.LoadBoardDashboard })));
const HOSTrackingDashboard = lazy(() => import('./pages/otr').then(m => ({ default: m.HOSTrackingDashboard })));
const GateSchedulesPage = lazy(() => import('./pages/GateSchedulesPage'));
const DnDDisputesPage = lazy(() => import('./pages/dnd-disputes/DnDDisputesPage'));
const CustomerPortalPage = lazy(() => import('./pages/portal/CustomerPortalPage'));
const CollaboratorCRM = lazy(() => import('./pages/collaborator/CollaboratorCRM'));
const CredentialRepairPage = lazy(() => import('./pages/public/CredentialRepairPage'));
const BrokerDashboard = lazy(() => import('./pages/broker/BrokerDashboard'));
const FreightMarginAI = lazy(() => import('./pages/ai/FreightMarginAI'));
const ProofOfDelivery = lazy(() => import('./pages/delivery/ProofOfDelivery'));
const DispatchMobileView = lazy(() => import('./pages/dispatch/DispatchMobileView'));
const MovingDashboard = lazy(() => import('./pages/moving/MovingDashboard'));
const MovingClosings = lazy(() => import('./pages/moving/MovingClosings'));
const MobileClosings = lazy(() => import('./pages/moving/MobileClosings'));
const WarehouseQueue = lazy(() => import('./pages/moving/WarehouseQueue'));
const MovingLayout = lazy(() => import('./pages/moving/MovingLayout'));
const ForemanMobileView = lazy(() => import('./pages/moving/ForemanMobileView'));

import { OwnerOpAuthCallback } from './pages/owner-operator/OwnerOpSignupPage';
import FloatingDebugPanel from './components/debug/FloatingDebugPanel';
import ImpersonationBanner from './components/ImpersonationBanner';
import BillingHoldGuard from './components/BillingHoldGuard';
import useFloatingOverlapDetector from './hooks/useFloatingOverlapDetector';
import { RoadsideCustomerPage, RoadsideProviderPage, RoadsidePaymentPage, RoadsideAdminPage, RoadsideTrackingPage, RoadsideHistoryPage, RoadsideVehiclesPage } from './pages/roadside';
import { Toaster } from './components/ui/sonner';
import { UpdateBanner } from './components/UpdateBanner';
import './App.css';

// Suspense fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-slate-950">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
  </div>
);

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user, isAuthenticated, testMode, isSuperAdmin, loading: authLoading } = useAuth();
  const { getSubscriptionType, loading: subscriptionLoading, hasFeature } = useSubscription();
  
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  
  // Wait for subscription to load before redirecting
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // Check if admin is pending approval
  if (user?.role === 'admin' && user?.approval_status === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }
  
  // Super admin in test mode - redirect based on test role
  if (isSuperAdmin && testMode) {
    if (testMode === 'driver') {
      return <Navigate to="/driver" replace />;
    }
    if (testMode === 'admin' || testMode === 'dispatcher') {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  // Super admin (not in test mode) goes to super admin dashboard
  if (user?.role === 'super_admin' && !testMode) {
    return <Navigate to="/super-admin" replace />;
  }

  // Marketplace-only accounts stay in the marketplace.
  if (['marketplace_customer', 'marketplace_vendor'].includes(user?.role)) {
    return <Navigate to="/marketplace" replace />;
  }
  
  // Drivers go to driver view
  if (user?.role === 'driver') {
    return <Navigate to="/driver" replace />;
  }

  // iter 304.15 — Foreman lands on the mobile-first Foreman view.
  if (user?.role === 'foreman') {
    // Crew (drivers + foremen) share one mobile app: Closings + My Moves.
    return <Navigate to="/moving/closings/mobile" replace />;
  }

  // Mechanic lands on the dedicated workbench
  if (user?.role === 'mechanic') {
    return <Navigate to="/mechanic" replace />;
  }
  
  // Moving-company back office lands on /moving on every device (phones included).
  if (isMovingTenantUser(user) && MOVING_BACK_OFFICE_ROLES.includes(user?.role)) {
    return <Navigate to="/moving" replace />;
  }

  // Dispatchers on mobile devices — show choice page
  if ((user?.role === 'dispatcher' || user?.role === 'admin') && window.innerWidth <= 768) {
    return <Navigate to="/dispatch-mobile" replace />;
  }

  // iter 304.12 / 306.13 — Moving-industry tenants land on the Moving module
  // by default, not the container-dispatch Imports dashboard. Drivers /
  // mechanics / super-admin already routed above; this is scoped to
  // admin/dispatcher/accounting/office who otherwise fall through to
  // /dashboard. We check THREE signals so a tenant that's a moving company
  // but has one of these flags missing still lands in the right place:
  //   1. explicit industry_type / company_category on the user object
  //   2. `moving_closings` feature active in the SubscriptionContext
  //   3. OR dispatch_core feature NOT active (purely moving-only tenants).
  const isMovingTenant =
    user?.industry_type === 'moving'
    || user?.company_category === 'moving_company'
    || (hasFeature && hasFeature('moving_closings') && !hasFeature('dispatch_core'));
  const movingBackOffice = MOVING_BACK_OFFICE_ROLES.includes(user?.role);
  if (isMovingTenant && movingBackOffice) {
    return <Navigate to="/moving" replace />;
  }

  // Check subscription type for standalone users
  const subscriptionType = getSubscriptionType();
  
  // TimeClock Pro standalone users go to timeclock page
  if (subscriptionType === 'standalone_timeclock') {
    return <Navigate to="/timeclock" replace />;
  }
  
  // PayTrack standalone users go to paytrack page
  if (subscriptionType === 'standalone_accounting') {
    return <Navigate to="/paytrack" replace />;
  }
  
  // Full subscription or default - go to dashboard
  return <Navigate to="/dashboard" replace />;
};

// Component to handle session_id in URL
const AppRouter = () => {
  const location = useLocation();
  
  // Dynamic manifest injection based on route
  useEffect(() => {
    const updateManifest = () => {
      const path = location.pathname;
      let manifestUrl = '/manifest.json';
      let themeColor = '#3b82f6';
      let appTitle = 'Drayly Dispatch';
      
      if (path.startsWith('/owner-operator') || path.startsWith('/owner-op')) {
        manifestUrl = '/manifest-owner-op.json';
        themeColor = '#10b981';
        appTitle = 'Drayly Owner Operator';
      } else if (path.startsWith('/timeclock')) {
        manifestUrl = '/manifest-timeclock.json';
        themeColor = '#f59e0b';
        appTitle = 'TimeClock Pro';
      } else if (path.startsWith('/driver')) {
        manifestUrl = '/manifest-driver.json';
        themeColor = '#10b981';
        appTitle = 'Drayly Driver';
      } else if (path.startsWith('/dispatch-mobile') || path.startsWith('/dashboard') || path.startsWith('/messages')) {
        manifestUrl = '/manifest.json';
        themeColor = '#3b82f6';
        appTitle = 'Drayly Dispatch';
      }
      
      // Update manifest link
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink && manifestLink.href !== manifestUrl) {
        manifestLink.href = manifestUrl;
      }
      
      // Update theme-color meta tag
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta) {
        themeColorMeta.content = themeColor;
      }
      
      // Update apple-mobile-web-app-title
      const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (appleTitleMeta) {
        appleTitleMeta.content = appTitle;
      }
    };
    
    updateManifest();
  }, [location.pathname]);
  
  // Check URL fragment for session_id BEFORE rendering routes
  // This must happen synchronously to prevent race conditions
  // Also check search params for compatibility
  // Use window.location directly to ensure we capture the hash (react-router can miss it in some edge cases)
  const hash = window.location.hash || location.hash || '';
  const search = window.location.search || location.search || '';
  const fullUrl = window.location.href;
  
  // Debug logging (dev only — spams console in production otherwise)
  if (process.env.NODE_ENV !== 'production') {
    console.log('AppRouter: Checking for session_id', { hash, search, fullUrl });
  }
  
  // Check multiple patterns for session_id
  const hasSessionId = hash.includes('session_id=') || 
                       search.includes('session_id=') || 
                       fullUrl.includes('session_id=');
  
  if (hasSessionId) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('AppRouter: session_id detected, rendering AuthCallback');
    }
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/home" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<ModularPricingPage />} />
      <Route path="/pricing-legacy" element={<PricingPage />} />
      <Route path="/standalone-pricing" element={<StandalonePricingPage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/marketplace/login" element={<MarketplaceAuthPage />} />
      <Route path="/marketplace/browse" element={<PublicMarketplacePage />} />
      <Route path="/marketplace/vendor/signup" element={<VendorSignupPage />} />
      <Route path="/marketplace/community" element={<CommunityPage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      
      {/* Customer Portal - Public (magic link access) */}
      <Route path="/portal/:token" element={<CustomerPortalPage />} />
      <Route path="/collaborator/crm" element={<CollaboratorCRM />} />

      {/* iter 264d.6.3 — Public credential repair page (one-time signed link) */}
      <Route path="/repair/cred/:token" element={<CredentialRepairPage />} />
      
      {/* Subscription Success - needs auth to verify */}
      <Route 
        path="/subscription/success" 
        element={
          <ProtectedRoute>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Auth callback route - explicit handling */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Company Onboarding */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <CompanyOnboarding />
          </ProtectedRoute>
        }
      />
      
      {/* Friendly fallback for users whose role doesn't match a route's
          allowedRoles. ProtectedRoute redirects here on mismatch — without
          this route declared the catchall would loop them back into
          RoleBasedRedirect ⇢ blank-screen + Max update depth errors. */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'accounting', 'super_admin']}>
            <MainLayout>
              <DashboardWrapper />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/moving"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'accounting', 'super_admin', 'foreman', 'carrier']}>
            <MainLayout>
              <MovingDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* iter 294: dedicated top-level /moving route — wrapped in MovingLayout
          so Closings + Warehouse now render as tabs INSIDE Moving. */}
      <Route
        path="/moving"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'accounting', 'super_admin', 'foreman', 'carrier']}>
            <MainLayout>
              <MovingLayout>
                <MovingDashboard />
              </MovingLayout>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/moving/crew/messages"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'foreman', 'super_admin']}>
            <div className="min-h-screen flex flex-col bg-slate-950">
              <CrewTabs />
              <div className="flex-1 min-h-0"><Messaging /></div>
            </div>
          </ProtectedRoute>
        }
      />
      <Route
        path="/moving/crew"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'foreman', 'super_admin']}>
            <ForemanMobileView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/moving/foreman"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'foreman', 'super_admin']}>
            <MainLayout>
              <MovingLayout>
                <ForemanMobileView />
              </MovingLayout>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* iter 294.6 — Moving Closings — contract + handover checklist sibling of MovingDashboard */}
      {/* iter 304.23 — Drivers are excluded: the office Closings dashboard
          shows every trip in the company. Drivers only see their own
          jobs via /moving/closings/mobile (below). */}
      <Route
        path="/moving/closings"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'accounting', 'foreman', 'super_admin']}>
            <MainLayout>
              <MovingLayout>
                <MovingClosings />
              </MovingLayout>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* iter 294.9 — Phase 4: office Warehouse Queue for last-mile assignment */}
      <Route
        path="/moving/warehouse-queue"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'accounting', 'super_admin']}>
            <MainLayout>
              <MovingLayout>
                <WarehouseQueue />
              </MovingLayout>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* iter 294.8 — Driver Mobile view of Closings (no MainLayout — mobile-first) */}
      <Route
        path="/moving/closings/mobile"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'foreman', 'carrier', 'super_admin']}>
            <MobileClosings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-tables"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <MainLayout>
              <MasterTables />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment-issues"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'super_admin', 'op', 'mechanic']}>
            <MainLayout>
              <EquipmentIssuesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment-issues/ai-dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'mechanic']}>
            <MainLayout>
              <AITriageDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mechanic"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'super_admin', 'op', 'mechanic']}>
            <MainLayout>
              <MechanicDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace/checkout"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace/checkout/success"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <CheckoutSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace/history"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <MainLayout>
              <BookingsHistoryPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace/vendor/analytics"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <VendorAnalyticsDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Broker TMS Route */}
      <Route
        path="/broker"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <MainLayout>
              <BrokerDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* FreightMargin AI Route */}
      <Route
        path="/ai/freight-margin"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin', 'owner_operator']}>
            <MainLayout>
              <FreightMarginAI />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Proof of Delivery Route */}
      <Route
        path="/pod"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'super_admin', 'owner_operator']}>
            <MainLayout>
              <ProofOfDelivery />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Roadside Rescue Routes */}
      <Route
        path="/roadside"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'super_admin', 'owner_operator', 'marketplace_customer']}>
            <MainLayout>
              <RoadsideCustomerPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roadside/provider"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'super_admin', 'owner_operator']}>
            <MainLayout>
              <RoadsideProviderPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roadside/payment/:requestId"
        element={
          <ProtectedRoute>
            <RoadsidePaymentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roadside/admin"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <RoadsideAdminPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roadside/track/:requestId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RoadsideTrackingPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Owner Operator Route */}
      <Route
        path="/owner-operator"
        element={
          <ProtectedRoute allowedRoles={['driver', 'admin', 'super_admin']}>
            <MainLayout>
              <OwnerOperatorDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/roadside/history"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RoadsideHistoryPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/roadside/vehicles"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RoadsideVehiclesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Owner Operator Signup (Public) */}
      <Route path="/owner-op-signup" element={<OwnerOpSignupPage />} />
      <Route path="/owner-op-signup/callback" element={<OwnerOpAuthCallback />} />
      
      <Route
        path="/messages"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'accounting', 'super_admin']}>
            <MainLayout>
              <Messaging />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/track-drivers"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <MainLayout>
              <TrackDriver />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gps-history"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <MainLayout>
              <DriverGPSHistory />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gps-history/:driverId"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <MainLayout>
              <DriverGPSHistory />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver"
        element={
          <ProtectedRoute allowedRoles={['driver', 'super_admin']}>
            <DriverView />
          </ProtectedRoute>
        }
      />
      
      {/* Dispatch Mobile View */}
      <Route
        path="/dispatch-mobile"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <Suspense fallback={<PageLoader />}>
              <DispatchMobileView />
            </Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Accounting Route */}
      <Route
        path="/accounting"
        element={
          <ProtectedRoute allowedRoles={['admin', 'accounting', 'super_admin']}>
            <MainLayout>
              <AccountingPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* System Settings Route - Admin only */}
      <Route
        path="/system"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <SystemSettingsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Terminal Integration Route - Admin only */}
      <Route
        path="/terminal-integration"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <TerminalIntegration />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* iter 252: /settings/terminals alias — internal banners and external
          docs link here, but the actual route was only registered as
          /terminal-integration which caused the unknown-path catchall to
          redirect to /home. */}
      <Route
        path="/settings/terminals"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <TerminalIntegration />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Data Migration Route - Admin only */}
      <Route
        path="/data-migration"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <DataMigration />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Equipment Compliance Route - Admin only */}
      <Route
        path="/compliance"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <EquipmentCompliance />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* OTR Module Routes */}
      <Route
        path="/otr/load-board"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'super_admin']}>
            <MainLayout>
              <LoadBoardDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/otr/hos"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'super_admin']}>
            <MainLayout>
              <HOSTrackingDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Gate Schedules */}
      <Route
        path="/gate-schedules"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'driver', 'owner', 'super_admin']}>
            <MainLayout>
              <GateSchedulesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* D&D Dispute Dashboard (iter 257 — Phase 1) */}
      <Route
        path="/dnd-disputes"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'owner', 'super_admin']}>
            <MainLayout>
              <DnDDisputesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Standalone Product Routes */}
      <Route
        path="/timeclock"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <TimeClockPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/paytrack"
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <MainLayout>
              <PayTrackPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Super Admin Routes */}
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Debug Dashboard - Super Admin only */}
      <Route
        path="/debug"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <DebugDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Pending Approval Page for Admins */}
      <Route
        path="/pending-approval"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <PendingApprovalPage />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Requests Page */}
      <Route
        path="/admin-requests"
        element={
          <ProtectedRoute allowedRoles={['admin', 'dispatcher', 'op', 'super_admin']}>
            <MainLayout>
              <AdminRequestsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      {/* Default redirect - role based */}
      <Route path="/" element={<RoleBasedRedirect />} />
      <Route path="*" element={<RoleBasedRedirect />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  // iter 259 R3 — dev-only console warning if two fixed floating elements
  // collide in the same corner with the same z-index.
  useFloatingOverlapDetector();
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <WebSocketProvider>
            <PresenceProvider>
              <CompanySettingsProvider>
                <SubscriptionProvider>
                  <ImpersonationBanner />
                  <BillingHoldGuard>
                    <AppRouter />
                  </BillingHoldGuard>
                  <Toaster position="top-right" richColors />
                  <UpdateBanner />
                  <FloatingDebugPanel />
                </SubscriptionProvider>
              </CompanySettingsProvider>
            </PresenceProvider>
          </WebSocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
